import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Container,
  useTheme,
  useMediaQuery,
  Divider
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AppTheme from '../../shared-theme/AppTheme';
import { FormProvider, useFormContext } from './FormContext';
import SubmissionFeedback from '../../components/SubmissionFeedback';
import Page1Company from './Page1Company';
import Page2Assistant from './Page2Assistant';
import Page3Knowledge from './Page3Knowledge';
import Page4Objectives from './Page4Objectives';
import Page5Limits from './Page5Limits';
import Page6Review from './Page6Review';
import cabanaLogoWhite from '../../images/logonextcabana-white.svg';
import cabanaLogoDark from '../../images/logonextcabana-dark.svg';

// Wrapper component that uses the form context
const FormContent: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(0);
  const {
    validateStep,
    resetErrors,
    submitForm,
    submissionStatus,
    submissionError,
    setFormData,
    resetSubmission
  } = useFormContext();  

  const steps = [
    'Identidade da Empresa',
    'Personalidade do Assistente',
    'Conhecimento Essencial',
    'Objetivos e Ações',
    'Limites e Escalonamento',
    'Revisão e Confirmação'
  ];

  const handlePrimaryAction = useCallback(async () => {
    const isStepValidForAction = activeStep === steps.length - 1 ? true : validateStep(activeStep);

    if (isStepValidForAction) {
      if (activeStep < steps.length - 1) {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        window.scrollTo(0, 0);
        resetErrors();
      }
      else {
        await submitForm();
      }
    }
  }, [activeStep, validateStep, resetErrors, submitForm, steps.length]);

  const handleBack = useCallback(() => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    window.scrollTo(0, 0);
    resetErrors();
  }, [resetErrors]);

  const handleResetForm = useCallback(() => {
    setFormData({
      businessName: '',
      businessIndustry: '',
      businessDescription: '',
      targetAudience: '',
      businessDifferentials: '',
      assistantName: '',
      toneOfVoice: [],
      emojiUsage: '',
      clientAddressing: '',
      productsServices: [{ name: '', description: '', price: '' }],
      workingHours: '',
      businessLocation: '',
      googleMapsLink: '',
      phoneContact: '',
      emailContact: '',
      websiteUrl: '',
      socialMedia: '',
      paymentMethods: '',
      deliveryAreas: '',
      deliveryFee: '',
      deliveryTime: '',
      mainObjective: '',
      faqs: [{ question: '', answer: '' }],
      leadCapture: { name: false, phone: false, email: false, interest: false, other: '' },
      appointmentTool: '',
      forbiddenTopics: '',
      transferConditions: [],
      transferMessage: ''
    });
    setActiveStep(0);
  }, [setFormData]);

  const handleEditSection = (sectionIndex: number) => {
    setActiveStep(sectionIndex);
    window.scrollTo(0, 0);
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: return <Page1Company />;
      case 1: return <Page2Assistant />;
      case 2: return <Page3Knowledge />;
      case 3: return <Page4Objectives />;
      case 4: return <Page5Limits />;
      case 5: return <Page6Review onEditSection={handleEditSection} />;
      default: return <div>Etapa não encontrada</div>;
    }
  };

  return (
    <>
      {/* --- CORREÇÃO 1: Desativar padding do container em telas pequenas --- */}
      <Container 
        maxWidth="md" 
        disableGutters={isMobile} // Remove o padding horizontal (gutters) em mobile
        sx={{
          // Garante que o container se ajuste à tela em mobile
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center', // Centraliza o Paper verticalmente em telas maiores
        }}
      >
        <Paper 
          elevation={isMobile ? 0 : 3} // Remove a sombra em mobile
          sx={{ 
            // --- CORREÇÃO 2: Ajustar estilos do Paper para mobile ---
            p: { xs: 2, sm: 3, md: 4 }, 
            my: { xs: 0, sm: 4 }, // Remove margem vertical em mobile
            mx: { xs: 0, sm: 2 }, // Remove margem horizontal em mobile
            borderRadius: { xs: 0, sm: 2 }, // Remove o arredondamento em mobile
            boxShadow: { xs: 'none', sm: '0 8px 24px rgba(0, 0, 0, 0.1)'}, // Garante que não há sombra em mobile
            display: 'flex',
            flexDirection: 'column',
            flexGrow: isMobile ? 1 : 0, // Faz o paper crescer para ocupar a tela inteira em mobile
            background: theme => `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
          }}
        >
          <Box>
            <img 
              src={ theme.palette.mode === 'dark' ? cabanaLogoWhite : cabanaLogoDark} 
              // src={cabanaLogoWhite}
              alt="Cabana Logo" 
              style={{ 
                width: isMobile ? '200px' : '300px', 
                height: 'auto', 
                display: 'block', 
                margin: '0 auto 20px' // Centraliza a logo
              }} 
            />
          </Box>
          <Divider />
          <Box sx={{mt: 5}}>
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom 
              align="center" 
              color="primary"
              sx={{ fontWeight: 700, fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' }, mb: 2, mt: { xs: 2, sm: 0 } }}
            >
              Briefing para Criação do seu Assistente Inteligente Nextalk
            </Typography>
            
            <Typography 
              variant="subtitle1" 
              gutterBottom 
              align="center" 
              sx={{ mb: 4, color: 'text.secondary', maxWidth: '80%', mx: 'auto', fontSize: { xs: '0.875rem', md: '1rem' }}}
            >
              Bem-vindo(a) ao processo de criação do seu novo atendente virtual Nextalk. Para que ele possa representar sua marca da melhor forma e atender seus clientes com excelência, precisamos que você preencha este formulário com o máximo de calma e atenção.
            </Typography>

            <Stepper 
              activeStep={activeStep} 
              sx={{ mb: 4, display: { xs: 'none', sm: 'flex' }}}
            >
              {steps.map((label) => (
                <Step key={label}><StepLabel>{label}</StepLabel></Step>
              ))}
            </Stepper>
            
            <Box sx={{ display: { xs: 'flex', sm: 'none' }, mb: 3, justifyContent: 'center', alignItems: 'center', width: '100%' }}>
              <Typography variant="body1" fontWeight="medium" color="primary">
                Etapa {activeStep + 1} de {steps.length}: {steps[activeStep]}
              </Typography>
            </Box>

            <Box sx={{ p: { xs: 1, sm: 2 }, mb: 2, borderRadius: 1, backgroundColor: theme => alpha(theme.palette.primary.light, 0.05), transition: 'all 0.3s ease' }}>
              {renderStepContent()}
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 'auto', pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Button 
                onClick={handleBack}
                variant="outlined"
                color="primary"
                size={isMobile ? "medium" : "large"}
                sx={{ minWidth: isMobile ? '100px' : '120px', visibility: activeStep > 0 ? 'visible' : 'hidden' }}
                disabled={submissionStatus === 'submitting'}
              >
                Voltar
              </Button>

              <Button
                onClick={handlePrimaryAction}
                variant="contained"
                color="primary"
                size={isMobile ? "medium" : "large"}
                disabled={submissionStatus === 'submitting'}
                sx={{ 
                  minWidth: isMobile ? '100px' : '120px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  '&:hover': { boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)' }
                }}
              >
                {activeStep === steps.length - 1 ? 'Finalizar Briefing' : 'Próximo'}
              </Button>
            </Box>
          </Box>
        </Paper>
        
        <SubmissionFeedback 
          status={submissionStatus}
          error={submissionError}
          onClose={() => {
            if (submissionStatus === 'success') {
              handleResetForm();
            }
            resetSubmission();
          }}
        />
      </Container>
    </>
  );
};

const MainPageForm: React.FC = () => {
  return (
    <AppTheme>
      <FormProvider>
        <FormContent />
      </FormProvider>
    </AppTheme>
  );
};

export default MainPageForm;